'use client';

import FadeIn from '@/components/FadeIn/FadeIn';
import Link from 'next/link';
import * as React from 'react';
import { usePostHog } from 'posthog-js/react';
import { BOOK_DEMO_HREF, SALES_DEMO_HREF } from '@/constants/publicConversionPaths';

/** Labeled the way a buyer scans a stack: what it is, then what kind of tool it is. */
const INTEGRATIONS = [
  { name: 'Salesforce', category: 'CRM' },
  { name: 'HubSpot', category: 'CRM' },
  { name: 'Slack', category: 'Alerts' },
  { name: 'Microsoft Teams', category: 'Alerts' },
  { name: 'Google Workspace', category: 'Email & calendar' },
  { name: 'Microsoft 365', category: 'Email & calendar' },
  { name: 'LinkedIn', category: 'Outreach' },
  { name: 'Sales Navigator', category: 'Prospecting' },
  { name: 'Twilio', category: 'Dialer' },
  { name: 'Gong', category: 'Call recording' },
  { name: 'ZoomInfo', category: 'Data' },
  { name: '6sense', category: 'Intent' },
];

const GoToMarket: React.FunctionComponent = () => {
  const posthog = usePostHog();

  return (
    <section className='market-section pt-10 tablet:pt-20 pb-20 tablet:pb-24'>
      <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
        <div className='bg-[#F2F6FF] rounded-[20px] p-8 md:p-10 lg:p-12 flex flex-col md:flex-row gap-8 md:gap-12 relative'>
          <div className='flex-1 md:w-1/2 flex flex-col'>
            <FadeIn variant='right' delay={0.35} className='flex flex-col h-full'>
              <div className='flex-grow'>
                <h2
                  className='leading-tight text-black font-medium'
                  style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.75rem)' }}
                >
                  Works with your <span style={{ color: '#0848E7' }}>CRM</span>, or without it
                </h2>
                <p
                  className='text-slate-600 mt-4 md:mt-5 leading-relaxed'
                  style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
                >
                  Connect Salesforce, HubSpot, Slack and Teams when you&apos;re ready. Teams without
                  CRM access still get identified accounts, research and outreach from day one.
                </p>
              </div>
              <div className='mt-8 hidden items-center gap-5 md:flex md:mt-10'>
                <Link
                  href={BOOK_DEMO_HREF}
                  className='inline-flex items-center justify-center h-[50px] min-w-[170px] px-7 text-base font-medium text-white rounded-[4px] transition-colors duration-200'
                  style={{ backgroundColor: '#0848E7' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#073dcc';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#0848E7';
                  }}
                  onClick={() =>
                    posthog?.capture('homepage_goto_market_cta_clicked', {
                      cta: 'book_tailored_demo',
                    })
                  }
                >
                  See your stack connected
                </Link>
                <Link
                  href={SALES_DEMO_HREF}
                  className='text-sm font-medium text-[#0848E7] underline-offset-4 hover:underline'
                  onClick={() =>
                    posthog?.capture('homepage_goto_market_cta_clicked', {
                      cta: 'watch_demo',
                    })
                  }
                >
                  Watch 4-minute demo →
                </Link>
              </div>
            </FadeIn>
          </div>
          <div className='flex-1 md:w-1/2 md:flex md:items-center md:justify-end'>
            <ul className='grid w-full grid-cols-2 gap-2 sm:grid-cols-3' aria-label='Integrations'>
              {INTEGRATIONS.map((item) => (
                <li
                  key={item.name}
                  className='rounded-xl border border-slate-200 bg-white px-4 py-3'
                >
                  <span className='block text-[15px] font-medium text-slate-900'>{item.name}</span>
                  <span className='block text-xs uppercase tracking-wide text-slate-500'>
                    {item.category}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoToMarket;
